// Social Hub — Gérez tous vos réseaux sociaux depuis AbracadaWorld
// Publiez une fois, diffusez partout. Commentaires et messages au même endroit.

import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, ExternalLink, Check, RefreshCw, AlertCircle,
  MessageCircle, Heart, Share2, Users, TrendingUp,
  Settings, Link2, Unlink, Info, Globe,
  ChevronRight, BarChart3, Bell, Send, Eye,
  Plus, Sparkles, Zap, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES RÉSEAUX
// ══════════════════════════════════════════════════════════════════════════════

const NETWORKS = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    color: '#0A66C2',
    bgLight: '#EBF5FF',
    borderColor: '#BFDBFE',
    description: 'Réseau professionnel B2B. Idéal pour les artisans et professionnels du bâtiment.',
    features: ['Publications', 'Articles', 'Commentaires', 'Messages privés'],
    available: true,
    icon: () => (
      <svg viewBox="0 0 24 24" fill="#0A66C2" width="28" height="28">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    stats_placeholder: { followers: '—', posts: '—', engagement: '—' },
  },
  {
    id: 'instagram',
    name: 'Instagram',
    color: '#E4405F',
    bgLight: '#FFF1F2',
    borderColor: '#FECDD3',
    description: 'Partagez vos photos de chantiers, avant/après et réels pour toucher le grand public.',
    features: ['Photos', 'Réels', 'Stories', 'Commentaires'],
    available: false,
    comingSoon: true,
    icon: () => (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <defs>
          <radialGradient id="ig1" cx="30%" cy="107%" r="150%">
            <stop offset="0%" stopColor="#fdf497"/>
            <stop offset="5%" stopColor="#fdf497"/>
            <stop offset="45%" stopColor="#fd5949"/>
            <stop offset="60%" stopColor="#d6249f"/>
            <stop offset="90%" stopColor="#285AEB"/>
          </radialGradient>
        </defs>
        <path fill="url(#ig1)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    stats_placeholder: { followers: '—', posts: '—', engagement: '—' },
  },
  {
    id: 'facebook',
    name: 'Facebook',
    color: '#1877F2',
    bgLight: '#EFF6FF',
    borderColor: '#BFDBFE',
    description: 'Publiez sur votre page pro, partagez des événements et recevez les avis clients.',
    features: ['Posts', 'Photos', 'Événements', 'Avis', 'Commentaires'],
    available: false,
    comingSoon: true,
    icon: () => (
      <svg viewBox="0 0 24 24" fill="#1877F2" width="28" height="28">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    stats_placeholder: { followers: '—', posts: '—', engagement: '—' },
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    color: '#000000',
    bgLight: '#F9FAFB',
    borderColor: '#E5E7EB',
    description: 'Touchez la nouvelle génération avec vos réels avant/après et vidéos de chantiers.',
    features: ['Vidéos courtes', 'Réels', 'Duets', 'Commentaires'],
    available: false,
    comingSoon: true,
    icon: () => (
      <svg viewBox="0 0 24 24" width="28" height="28">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.28 6.28 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.54V6.79a4.85 4.85 0 01-1.02-.1z"/>
      </svg>
    ),
    stats_placeholder: { followers: '—', posts: '—', engagement: '—' },
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    color: '#000000',
    bgLight: '#F9FAFB',
    borderColor: '#E5E7EB',
    description: 'Partagez votre expertise, commentez l\'actu du bâtiment et développez votre notoriété.',
    features: ['Posts', 'Threads', 'Réponses', 'Messages'],
    available: false,
    comingSoon: true,
    requiresPaid: true,
    paidNote: "L'API X nécessite un abonnement payant ($100/mois).",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="#000000" width="28" height="28">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.629 5.905-5.629zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    stats_placeholder: { followers: '—', posts: '—', engagement: '—' },
  },
  {
    id: 'youtube',
    name: 'YouTube',
    color: '#FF0000',
    bgLight: '#FFF1F2',
    borderColor: '#FECDD3',
    description: 'Publiez vos tutoriels, présentations de chantiers et témoignages clients.',
    features: ['Vidéos', 'Shorts', 'Live', 'Commentaires'],
    available: false,
    comingSoon: true,
    icon: () => (
      <svg viewBox="0 0 24 24" fill="#FF0000" width="28" height="28">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    stats_placeholder: { followers: '—', posts: '—', engagement: '—' },
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT CARTE RÉSEAU
// ══════════════════════════════════════════════════════════════════════════════

function NetworkCard({ network, connectedAccount, onConnect, onDisconnect }) {
  const IconComponent = network.icon;
  const isConnected = !!connectedAccount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border overflow-hidden"
      style={{ borderColor: isConnected ? network.borderColor : '#E5E7EB' }}
    >
      {/* Header carte */}
      <div
        className="px-5 pt-5 pb-4"
        style={{ background: isConnected ? network.bgLight : 'white' }}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center">
              <IconComponent />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900">{network.name}</h3>
                {!network.available && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
                    <Lock size={9} />
                    Bientôt
                  </span>
                )}
                {network.requiresPaid && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-600">
                    API payante
                  </span>
                )}
              </div>
              {isConnected ? (
                <p className="text-sm font-medium mt-0.5" style={{ color: network.color }}>
                  ✓ Connecté — {connectedAccount.name}
                </p>
              ) : (
                <p className="text-xs text-gray-400 mt-0.5">Non connecté</p>
              )}
            </div>
          </div>

          {isConnected && (
            <button
              onClick={() => onDisconnect(network.id)}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-500"
              title="Déconnecter"
            >
              <Unlink size={16} />
            </button>
          )}
        </div>

        {/* Stats si connecté */}
        {isConnected && (
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { label: 'Abonnés', value: connectedAccount.followers_count || '—' },
              { label: 'Publications', value: connectedAccount.posts_count || '—' },
              { label: 'Engagement', value: connectedAccount.engagement_rate ? `${connectedAccount.engagement_rate}%` : '—' },
            ].map(stat => (
              <div key={stat.label} className="text-center">
                <p className="font-bold text-gray-900 text-lg leading-tight">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Description + features */}
      <div className="px-5 py-4 border-t border-gray-50">
        <p className="text-sm text-gray-600 leading-relaxed mb-3">{network.description}</p>
        <div className="flex flex-wrap gap-1.5">
          {network.features.map(f => (
            <span key={f} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">{f}</span>
          ))}
        </div>
        {network.requiresPaid && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <AlertCircle size={11} />
            {network.paidNote}
          </p>
        )}
      </div>

      {/* Bouton */}
      <div className="px-5 pb-5">
        {isConnected ? (
          <div className="flex gap-2">
            <button className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center gap-1.5">
              <Settings size={14} />
              Paramètres
            </button>
            <button className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5">
              <BarChart3 size={14} />
              Statistiques
            </button>
          </div>
        ) : network.available ? (
          <button
            onClick={() => onConnect(network)}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-98 flex items-center justify-center gap-2"
            style={{ background: network.color }}
          >
            <Link2 size={15} />
            Connecter {network.name}
          </button>
        ) : (
          <button
            disabled
            className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Lock size={14} />
            Bientôt disponible
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT ACTIVITÉ UNIFIÉE (verrouillé si aucun compte connecté)
// ══════════════════════════════════════════════════════════════════════════════

function UnifiedActivityFeed({ connectedNetworks, activities }) {
  if (connectedNetworks.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Lock size={28} className="text-gray-400" />
        </div>
        <h3 className="font-bold text-gray-900 mb-2">Fil d'activité unifié</h3>
        <p className="text-sm text-gray-500 max-w-xs mx-auto mb-5">
          Connectez au moins un réseau social pour voir tous vos commentaires,
          mentions et messages au même endroit.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {['Commentaires', 'Mentions', 'Messages privés', 'Likes', 'Partages'].map(f => (
            <span key={f} className="text-xs px-3 py-1 bg-gray-100 text-gray-500 rounded-full">{f}</span>
          ))}
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <RefreshCw size={24} className="text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-500">Chargement de l'activité...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
      {activities.map((activity, i) => (
        <div key={i} className="p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            {activity.type === 'comment' && <MessageCircle size={16} className="text-blue-500" />}
            {activity.type === 'like' && <Heart size={16} className="text-red-500" />}
            {activity.type === 'share' && <Share2 size={16} className="text-green-500" />}
            {activity.type === 'mention' && <Bell size={16} className="text-orange-500" />}
            {activity.type === 'dm' && <Send size={16} className="text-purple-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900">{activity.text}</p>
            <p className="text-xs text-gray-400 mt-0.5">{activity.time} · {activity.network}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════════

export default function SocialHubPage() {
  const { user } = useOutletContext() || {};
  const navigate = useNavigate();

  const [connectedAccounts, setConnectedAccounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [connectingNetwork, setConnectingNetwork] = useState(null);

  const connectedNetworksList = Object.keys(connectedAccounts);
  const connectedCount = connectedNetworksList.length;

  // Charger les comptes connectés
  useEffect(() => {
    loadConnectedAccounts();
  }, []);

  const loadConnectedAccounts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('client_token') || localStorage.getItem('pro_token');
      if (!token) return;
      const res = await fetch(`${API_URL}/api/social/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const accountsMap = {};
        (data.accounts || []).forEach(acc => { accountsMap[acc.network] = acc; });
        setConnectedAccounts(accountsMap);

        // Charger l'activité si des comptes sont connectés
        if (data.accounts?.length > 0) {
          loadActivity(token);
        }
      }
    } catch (e) {
      // Pas de comptes connectés
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/social/activity?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActivities(data.activities || []);
      }
    } catch (e) { /* ignoré */ }
  };

  const handleConnect = async (network) => {
    if (!network.available) {
      toast.info(`${network.name} sera disponible prochainement.`);
      return;
    }
    setConnectingNetwork(network.id);
    try {
      const token = localStorage.getItem('client_token') || localStorage.getItem('pro_token');
      const res = await fetch(`${API_URL}/api/social/${network.id}/authorize`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Ouvrir le popup OAuth
        const popup = window.open(data.auth_url, 'oauth', 'width=600,height=700,left=300,top=100');
        // Écouter le retour OAuth
        const handler = (event) => {
          if (event.data?.type === 'social_oauth_success' && event.data?.network === network.id) {
            window.removeEventListener('message', handler);
            toast.success(`${network.name} connecté avec succès !`);
            loadConnectedAccounts();
            setConnectingNetwork(null);
          }
        };
        window.addEventListener('message', handler);
        // Timeout cleanup
        setTimeout(() => {
          window.removeEventListener('message', handler);
          setConnectingNetwork(null);
        }, 120000);
      } else {
        const err = await res.json();
        toast.error(err.detail || `Erreur de connexion à ${network.name}`);
        setConnectingNetwork(null);
      }
    } catch (e) {
      toast.error(`Impossible de se connecter à ${network.name} pour l'instant.`);
      setConnectingNetwork(null);
    }
  };

  const handleDisconnect = async (networkId) => {
    if (!confirm(`Déconnecter ${networkId} ?`)) return;
    try {
      const token = localStorage.getItem('client_token') || localStorage.getItem('pro_token');
      const res = await fetch(`${API_URL}/api/social/accounts/${networkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Compte déconnecté');
        setConnectedAccounts(prev => {
          const next = { ...prev };
          delete next[networkId];
          return next;
        });
      }
    } catch (e) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  return (
    <div className="space-y-6 pb-8" data-testid="social-hub-page">

      {/* ── En-tête ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Réseaux Sociaux</h1>
          <p className="text-gray-500 mt-1">
            Publiez une fois, diffusez partout. Gérez tout depuis AbracadaWorld.
          </p>
        </div>
        {connectedCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-xl">
            <Check size={16} className="text-green-600" />
            <span className="text-sm font-semibold text-green-700">
              {connectedCount} réseau{connectedCount > 1 ? 'x' : ''} connecté{connectedCount > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* ── Bandeau informatif ── */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8F] rounded-2xl p-5 text-white">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">Le concept : publier une fois, diffuser partout</h3>
            <p className="text-white/80 text-sm leading-relaxed">
              Connectez vos réseaux sociaux et choisissez où chaque publication sera diffusée —
              votre réseau AbracadaWorld, votre boutique, LinkedIn, Instagram...
              Les commentaires et messages arrivent tous ici, au même endroit.
            </p>
          </div>
        </div>

        {/* Schéma visuel */}
        <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2">
            <Globe size={14} />
            <span className="text-xs font-medium">AbracadaWorld</span>
          </div>
          <ArrowRight size={16} className="text-white/50" />
          <div className="flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2">
            <Zap size={14} />
            <span className="text-xs font-medium">Une seule publication</span>
          </div>
          <ArrowRight size={16} className="text-white/50" />
          <div className="flex flex-wrap gap-2 justify-center">
            {['Ma boutique', 'LinkedIn', 'Instagram', '...'].map(n => (
              <span key={n} className="bg-white/15 rounded-lg px-2.5 py-1.5 text-xs font-medium">{n}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Grille des réseaux ── */}
      <div>
        <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Link2 size={18} className="text-[#1E3A5F]" />
          Connecter mes réseaux
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {NETWORKS.map((network, i) => (
            <motion.div
              key={network.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
            >
              <NetworkCard
                network={network}
                connectedAccount={connectedAccounts[network.id]}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Fil d'activité unifié ── */}
      <div>
        <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Bell size={18} className="text-[#1E3A5F]" />
          Activité récente
          {connectedCount === 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-400">
              <Lock size={9} />
              Verrouillé
            </span>
          )}
        </h2>
        <UnifiedActivityFeed
          connectedNetworks={connectedNetworksList}
          activities={activities}
        />
      </div>

      {/* ── Section messages ── */}
      <div>
        <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <MessageCircle size={18} className="text-[#1E3A5F]" />
          Messages privés
          {connectedCount === 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-400">
              <Lock size={9} />
              Verrouillé
            </span>
          )}
        </h2>

        {connectedCount === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-gray-400" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Messages unifiés</h3>
            <p className="text-sm text-gray-500 max-w-xs mx-auto">
              Recevez et répondez aux messages privés de tous vos réseaux connectés
              depuis cette page.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <MessageCircle size={24} className="text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Aucun nouveau message</p>
          </div>
        )}
      </div>

      {/* ── Statistiques cross-réseaux ── */}
      <div>
        <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 size={18} className="text-[#1E3A5F]" />
          Statistiques globales
          {connectedCount === 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-400">
              <Lock size={9} />
              Verrouillé
            </span>
          )}
        </h2>

        {connectedCount === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Abonnés total', icon: Users },
              { label: 'Publications', icon: Eye },
              { label: 'Interactions', icon: Heart },
              { label: 'Portée', icon: TrendingUp },
            ].map(({ label, icon: Icon }) => (
              <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5 text-center opacity-60">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Lock size={18} className="text-gray-400" />
                </div>
                <p className="text-2xl font-bold text-gray-300">—</p>
                <p className="text-xs text-gray-400 mt-1">{label}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Abonnés total', value: Object.values(connectedAccounts).reduce((sum, a) => sum + (a.followers_count || 0), 0), icon: Users, color: 'blue' },
              { label: 'Publications ce mois', value: '—', icon: Eye, color: 'green' },
              { label: 'Interactions', value: '—', icon: Heart, color: 'red' },
              { label: 'Portée estimée', value: '—', icon: TrendingUp, color: 'orange' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5 text-center">
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Guide de configuration LinkedIn ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-blue-900 mb-2">Comment connecter LinkedIn ?</h3>
            <ol className="text-sm text-blue-800 space-y-1.5">
              <li><span className="font-semibold">1.</span> Créez une app sur <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline font-medium">linkedin.com/developers/apps</a></li>
              <li><span className="font-semibold">2.</span> Activez les permissions <code className="bg-blue-100 px-1 rounded">w_member_social</code> et <code className="bg-blue-100 px-1 rounded">r_basicprofile</code></li>
              <li><span className="font-semibold">3.</span> Ajoutez en "Redirect URL" : <code className="bg-blue-100 px-1 rounded">{window.location.origin}/api/social/linkedin/callback</code></li>
              <li><span className="font-semibold">4.</span> Copiez le <strong>Client ID</strong> et <strong>Client Secret</strong> dans votre fichier <code className="bg-blue-100 px-1 rounded">.env</code> :</li>
            </ol>
            <div className="mt-3 bg-blue-900 text-blue-100 rounded-xl px-4 py-3 font-mono text-xs">
              <p>LINKEDIN_CLIENT_ID=votre_client_id</p>
              <p>LINKEDIN_CLIENT_SECRET=votre_client_secret</p>
              <p>LINKEDIN_REDIRECT_URI={window.location.origin}/api/social/linkedin/callback</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
